import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { RecipeV2Detail, DietaryAttribute, DietaryAttributeStatus, DietaryCategory, AllergenSource } from '../types';
import { UserContext } from '../../../components/shared/useUser';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import DietaryAttributeChips from './DietaryAttributeChips';

interface DietaryTabProps {
  recipe: RecipeV2Detail;
}

/** The four categories shown here - Allergen/Intolerance live on the Allergens tab instead so
 * the two tabs don't duplicate the same attributes. */
const DIETARY_TAB_CATEGORIES = [
  DietaryCategory.DietaryRequirement,
  DietaryCategory.LifestylePreference,
  DietaryCategory.NutritionalClaim,
  DietaryCategory.SourcingSustainability,
];

const lowerFirst = (value: string) => value.charAt(0).toLowerCase() + value.slice(1);

const DietaryTab = ({ recipe }: DietaryTabProps) => {
  const { t } = useTranslation('recipeV2');
  const { hasPermissionTo } = useContext(UserContext);
  const api = useRecipeV2Api();
  const canWrite = hasPermissionTo(['RecipeV2AllergenWrite']);
  const [allAttributes, setAllAttributes] = useState<DietaryAttribute[]>(recipe.dietaryAttributes ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const attributes = allAttributes.filter((a) => DIETARY_TAB_CATEGORIES.includes(a.category));

  // Override dialog
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<DietaryAttribute | null>(null);
  const [overrideStatus, setOverrideStatus] = useState<DietaryAttributeStatus>(DietaryAttributeStatus.FreeFrom);
  const [overrideReason, setOverrideReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Add dietary attribute dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addCategory, setAddCategory] = useState<DietaryCategory>(DietaryCategory.DietaryRequirement);
  const [addStatus, setAddStatus] = useState<DietaryAttributeStatus>(DietaryAttributeStatus.Suitable);

  useEffect(() => {
    setIsLoading(true);
    api.getDietaryAttributes(recipe.id)
      .then(setAllAttributes)
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [recipe.id, api]);

  const handleOpenOverride = (attr: DietaryAttribute) => {
    setSelectedAttribute(attr);
    setOverrideStatus(attr.status);
    setOverrideReason('');
    setOverrideDialogOpen(true);
  };

  const handleApplyOverride = async () => {
    if (!selectedAttribute) return;
    setIsSaving(true);
    try {
      await api.overrideDietaryAttribute(recipe.id, {
        attributeId: selectedAttribute.id,
        status: overrideStatus,
        reason: overrideReason,
      });
      setAllAttributes((prev) =>
        prev.map((a) => a.id === selectedAttribute.id ? { ...a, status: overrideStatus } : a),
      );
      setOverrideDialogOpen(false);
    } catch { /* noop */ }
    setIsSaving(false);
  };

  const handleOpenAdd = () => {
    setAddName('');
    setAddCategory(DietaryCategory.DietaryRequirement);
    setAddStatus(DietaryAttributeStatus.Suitable);
    setAddDialogOpen(true);
  };

  const handleConfirmAdd = () => {
    const name = addName.trim();
    if (!name) return;
    // lazy: client-side only, no backend persistence yet (matches overrideDietaryAttribute).
    setAllAttributes((prev) => [
      ...prev,
      {
        id: -Date.now(),
        code: name.toUpperCase().replace(/\s+/g, '_'),
        name,
        category: addCategory,
        status: addStatus,
        source: AllergenSource.Manual,
      },
    ]);
    setAddDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('loading')}</Typography>
      </Paper>
    );
  }

  if (attributes.length === 0) {
    return (
      <Stack spacing={3}>
        <Paper variant="outlined"
          sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('dietary.noAttributes')}</Typography>
        </Paper>
        {canWrite && (
          <Stack direction="row"
            justifyContent="flex-end">
            <Button startIcon={<Add />}
              variant="secondary"
              onClick={handleOpenAdd}>
              {t('dietary.add', 'Add dietary attribute')}
            </Button>
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Grouped display */}
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>{t('dietary.title')}</Typography>
        <DietaryAttributeChips
          attributes={attributes}
          categories={DIETARY_TAB_CATEGORIES}
          showName
          groupByCategory
          size="medium"
          spacing={1}
        />
      </Paper>

      {/* Override management table */}
      {canWrite && (
        <Paper variant="outlined"
          sx={{ p: 2 }}>
          <Stack direction="row"
            justifyContent="space-between"
            alignItems="center">
            <Typography variant="textLG"
              fontWeight={600}
              gutterBottom>{t('dietary.manage')}</Typography>
            <Button startIcon={<Add />}
              variant="secondary"
              size="md"
              onClick={handleOpenAdd}>
              {t('dietary.add', 'Add dietary attribute')}
            </Button>
          </Stack>
          <Stack spacing={1}>
            {attributes.map((attr) => (
              <Stack
                key={attr.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Typography variant="textSM">{attr.name} ({t(`dietary.categories.${lowerFirst(attr.category)}`)})</Typography>
                <Stack direction="row"
                  spacing={1}
                  alignItems="center">
                  <Typography variant="textSM"
                    color="text.secondary">{t(`dietary.${lowerFirst(attr.status)}`)}</Typography>
                  <Button
                    size="md"
                    startIcon={<Edit />}
                    variant="secondary"
                    onClick={() => handleOpenOverride(attr)}
                  >
                    {t('dietary.override')}
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Override dialog */}
      <Dialog open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('dietary.overrideTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            {selectedAttribute && (
              <Typography variant="textSM"
                fontWeight={600}>{selectedAttribute.name}</Typography>
            )}
            <Select
              value={overrideStatus}
              onChange={(e) => setOverrideStatus(e.target.value as DietaryAttributeStatus)}
              size="small"
              fullWidth
            >
              <MenuItem value={DietaryAttributeStatus.Contains}>{t('dietary.contains')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.MayContain}>{t('dietary.mayContain')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.FreeFrom}>{t('dietary.freeFrom')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.Suitable}>{t('dietary.suitable')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.NotSuitable}>{t('dietary.notSuitable')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.Certified}>{t('dietary.certified')}</MenuItem>
            </Select>
            <TextField
              multiline
              rows={3}
              fullWidth
              label={t('dietary.overrideReason')}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}
            variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={handleApplyOverride}
            variant="primary"
            disabled={isSaving || overrideReason.trim() === ''}
          >
            {t('dietary.overrideConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add dietary attribute dialog */}
      <Dialog open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('dietary.addTitle', 'Add dietary attribute')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}
            sx={{ mt: 1 }}>
            <TextField
              label={t('dietary.attributeName', 'Attribute name')}
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              fullWidth
            />
            <Select
              value={addCategory}
              onChange={(e) => setAddCategory(e.target.value as DietaryCategory)}
              size="small"
              fullWidth
            >
              {DIETARY_TAB_CATEGORIES.map((category) => (
                <MenuItem key={category}
                  value={category}>{t(`dietary.categories.${lowerFirst(category)}`)}</MenuItem>
              ))}
            </Select>
            <Select
              value={addStatus}
              onChange={(e) => setAddStatus(e.target.value as DietaryAttributeStatus)}
              size="small"
              fullWidth
            >
              <MenuItem value={DietaryAttributeStatus.Suitable}>{t('dietary.suitable')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.NotSuitable}>{t('dietary.notSuitable')}</MenuItem>
              <MenuItem value={DietaryAttributeStatus.Certified}>{t('dietary.certified')}</MenuItem>
            </Select>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}
            variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={handleConfirmAdd}
            variant="primary"
            disabled={addName.trim() === ''}
          >
            {t('allergens.addConfirm', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DietaryTab;
